import { authHeaders, throwOnError } from "./authHeaders";

/**
 * Client for the patrol-compliance API.
 *
 * Everything here reports STATUS. Nothing in this module assigns work, orders
 * checkpoints into a route, or produces a task list — see the header comment on
 * api/compliance/status.ts. Keep any UI built on it framed the same way.
 */

export type ComplianceStatus = "OK" | "DUE_SOON" | "OVERDUE" | "NEVER_SCANNED";

export interface CheckpointStatus {
  location_id: string;
  hospital_id: string;
  hospital_name: string | null;
  name: string;
  /** "3rd Floor · Tower B" — pre-joined by the API. */
  place: string;
  last_scanned_at: string | null;
  interval_minutes: number;
  min_scans_per_24h: number;
  rule_source: "LOCATION" | "HOSPITAL" | "SYSTEM";
  due_at: string | null;
  minutes_remaining: number | null;
  minutes_overdue: number;
  scans_last_24h: number;
  status: ComplianceStatus;
  countdown_label: string;
}

export interface ComplianceAlert {
  id: string;
  hospital_id: string;
  location_id: string | null;
  alert_type: string;
  severity: string;
  message: string;
  last_scan_at: string | null;
  minutes_overdue: number | null;
  created_at: string;
  location_name: string | null;
  hospital_name: string | null;
}

export interface ComplianceCounts {
  overdue: number;
  due_soon: number;
  ok: number;
  never_scanned: number;
  total: number;
}

/** 2-hour compliance rate: share of active checkpoints scanned in the last 2h. */
export interface ComplianceRate {
  interval_minutes?: number;
  total: number;
  compliant: number;
  non_compliant: number;
  overdue: number;
  never_scanned: number;
  /** Percentage, one decimal; null when there are no checkpoints. */
  rate: number | null;
}

export interface ShiftCoverage {
  key: "DAY" | "EVENING" | "NIGHT";
  label: string;
  start: string;
  end: string;
  total: number;
  scanned: number;
  not_scanned: number;
  rate: number | null;
}

export interface HospitalComplianceRate extends ComplianceRate {
  hospital_id: string;
  hospital_name: string | null;
}

export interface ComplianceHistoryPoint {
  /** ET bucket start, "YYYY-MM-DDTHH:MM". */
  period: string;
  avg_rate: number | null;
  min_rate: number | null;
  samples: number;
}

export interface ComplianceHistoryResponse {
  interval_minutes: number;
  start: string;
  end: string;
  bucket: "hour" | "day";
  overall: { avg_rate: number | null; min_rate: number | null; samples: number } | null;
  series: ComplianceHistoryPoint[];
  by_hospital: Array<{ hospital_id: string; hospital_name: string | null; avg_rate: number | null; min_rate: number | null; samples: number }>;
  /** One series per hospital over the same buckets (absent on older API versions). */
  series_by_hospital?: Array<{ hospital_id: string; hospital_name: string | null; points: ComplianceHistoryPoint[] }>;
  first_snapshot_at: string | null;
}

export async function fetchComplianceHistory(params: {
  hospitalId?: string;
  start?: string;
  end?: string;
  bucket?: "hour" | "day" | "auto";
} = {}): Promise<ComplianceHistoryResponse> {
  const query = new URLSearchParams();
  if (params.hospitalId) query.set("hospital_id", params.hospitalId);
  if (params.start) query.set("start", params.start);
  if (params.end) query.set("end", params.end);
  if (params.bucket) query.set("bucket", params.bucket);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`/api/compliance/history${suffix}`, { headers: authHeaders() });
  await throwOnError(response, "Failed to load compliance history");
  return response.json();
}

export interface ComplianceStatusResponse {
  generated_at: string;
  advisory: string;
  hospital_ids: string[];
  filter: string;
  counts: ComplianceCounts;
  /** Absent on very old API versions / empty scope. */
  rate?: ComplianceRate;
  rate_by_hospital?: HospitalComplianceRate[];
  /** Coverage of the current shift (Day 6–14 / Evening 14–22 / Night 22–6, ET). */
  shift?: ShiftCoverage;
  checkpoints: CheckpointStatus[];
  alerts: ComplianceAlert[];
  system_default: { interval_minutes: number; min_scans_per_24h: number };
  returned: number;
  truncated: boolean;
}

export interface ComplianceRule {
  id: string;
  hospital_id: string;
  location_id: string | null;
  interval_minutes: number;
  min_scans_per_24h: number;
  active: boolean;
  created_at: string;
  updated_at: string | null;
  hospital_name: string | null;
  location_name: string | null;
  floor: string | null;
  building: string | null;
}

export interface ComplianceRulesResponse {
  system_default: { interval_minutes: number; min_scans_per_24h: number };
  rules: ComplianceRule[];
}

export async function fetchComplianceStatus(params: {
  hospitalId?: string;
  status?: "all" | "attention" | "overdue";
  limit?: number;
} = {}): Promise<ComplianceStatusResponse> {
  const query = new URLSearchParams();
  if (params.hospitalId) query.set("hospital_id", params.hospitalId);
  if (params.status) query.set("status", params.status);
  if (params.limit) query.set("limit", String(params.limit));

  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`/api/compliance/status${suffix}`, {
    headers: authHeaders(),
  });
  await throwOnError(response, "Failed to load compliance status");
  return response.json();
}

export async function fetchComplianceRules(hospitalId?: string): Promise<ComplianceRulesResponse> {
  const suffix = hospitalId ? `?hospital_id=${encodeURIComponent(hospitalId)}` : "";
  const response = await fetch(`/api/compliance/rules${suffix}`, {
    headers: authHeaders(),
  });
  await throwOnError(response, "Failed to load compliance rules");
  return response.json();
}

export async function createComplianceRule(input: {
  hospital_id: string;
  location_id?: string | null;
  interval_minutes: number;
  min_scans_per_24h: number;
}): Promise<ComplianceRule> {
  const response = await fetch("/api/compliance/rules", {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(input),
  });
  await throwOnError(response, "Failed to create rule");
  const body = await response.json();
  return body.rule ?? body;
}

export async function updateComplianceRule(
  id: string,
  input: { interval_minutes?: number; min_scans_per_24h?: number; active?: boolean }
): Promise<ComplianceRule> {
  const response = await fetch(`/api/compliance/rules?id=${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: authHeaders(true),
    body: JSON.stringify(input),
  });
  await throwOnError(response, "Failed to update rule");
  const body = await response.json();
  return body.rule ?? body;
}

export async function deleteComplianceRule(id: string): Promise<void> {
  const response = await fetch(`/api/compliance/rules?id=${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  await throwOnError(response, "Failed to delete rule");
}
